import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, resource, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';

import { MilestoneTable } from '@lib/milestone-table';
import { PoSelect } from '@lib/po-select';
import { PurchasedHoursTable } from '@lib/purchased-hours-table';
import moment from 'moment';

import { Milestone, Product, Project, Purchased } from '../schemas';
import { AgreementService, Exporter, ProductService } from '../service';
import { ProjectsService } from '../service/projects';

export interface ExtendedProject extends Project {
  _Owner: { type: string; id: string };
  _Invoices: (Milestone | Purchased)[];
  _ToBeInvoiced: number;
  _OverdueInvoices: number;
  _ProductOwner: string;
}

function endOfQuarter() {
  const date = moment();
  if (date.month() === 11) {
    // December - roll over to next year
    date.year(date.year() + 1);
  }
  // Calculate quarter end: month 2, 5, 8, 11 (Mar, Jun, Sep, Dec)
  date.month(Math.floor(((date.month() + 1) % 12) / 3) * 3 + 1).endOf('month');
  return date;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DecimalPipe,
    DatePipe,
    CommonModule,
    MatIconModule,
    MatPaginatorModule,
    FormsModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatSortModule,
    MatTableModule,
    MatToolbarModule,
    MatTooltipModule,
    MatButtonModule,
    PoSelect,
    RouterModule,
    MilestoneTable,
    PurchasedHoursTable,
  ],
  templateUrl: './projects-page.html',
})
export class ProjectsPage {
  projectService = inject(ProjectsService);
  productService = inject(ProductService);
  agreementService = inject(AgreementService);
  exporter = inject(Exporter);

  productOwnerRouterMapping = {
    opportunity: 'products',
    agreement: 'agreements',
  };

  pending = signal(false);
  columnsToShow = signal(['Title', 'Description', 'StartDate', 'EndDate', '_ToBeInvoiced', '_ProductOwner', 'actions']);
  filterPo = signal('');

  toBeInvoicedTotal = computed(() => {
    return this.projects()
      .map((p) => p._ToBeInvoiced || 0)
      .reduce((sum, n) => sum + n, 0);
  });

  overdueInvoices = computed(() => {
    return this.projects()
      .map((p) => p._OverdueInvoices || 0)
      .reduce((sum, n) => sum + n, 0);
  });

  upcomingStart = signal(moment());
  upcomingEnd = signal(endOfQuarter());

  upcomingInvoices = computed(() => {
    if (!this.upcomingStart() || !this.upcomingEnd()) {
      return 0;
    }

    const invoices = this.projects()
      .flatMap((p) => p._Invoices || [])
      .filter(
        ({ Status, DueDate }) =>
          +Status !== 20 && // Not yet invoiced
          DueDate >= this.upcomingStart().format('Y-MM-DD') &&
          DueDate <= this.upcomingEnd().format('Y-MM-DD'),
      );

    // Sum values with currency conversion (non-AUD divided by rate)
    return invoices.reduce(
      (sum, { Value = 0, Currency, Rate }) => sum + (Currency === 'AUD' ? Value : Value / Rate),
      0,
    );
  });

  projectsResource = resource({
    params: () => ({
      agreements: this.agreementService.agreements(),
      products: this.productService.products(),
      projects: this.projectService.projects(),
    }),
    defaultValue: [],
    loader: async ({ params }) => {
      this.pending.set(true);
      let items = params.projects;

      const projectIds = new Set(items.map((p) => p.Id));

      // There are invoices attached directly to the product/agreement - they
      // will be added to fake projects with an empty ID. Additionally, invoices
      // from the removed projects also will be gathered there.
      items = items.concat(
        params.products.map((item) =>
          Project.parse({
            Id: `opportunity:${item.JobNumber}`,
            Owner: `opportunity:${item.JobNumber}`,
            Title: `Invoices for Project ${item.JobNumber}`,
            Description: 'Invoices that are apart of the original project',
          }),
        ),
        params.agreements.map((item) =>
          Project.parse({
            Id: `agreement:${item.AgreementNumber}`,
            Owner: `agreement:${item.AgreementNumber}`,
            Title: `Invoices for Project ${item.AgreementNumber}`,
            Description: 'Invoices that are apart of the original project',
          }),
        ),
      );
      items.sort((l, r) => l.Id!.localeCompare(r.Id!) || l.Title.localeCompare(r.Title));

      const result = await this.attachInvoices(items, projectIds);
      this.pending.set(false);
      return result;
    },
  });

  projects = computed(() => {
    let projects = this.projectsResource.value();

    const po = this.filterPo();
    if (po) {
      projects = projects.filter((p) => p._ProductOwner === po);
    }
    return projects;
  });

  sort = viewChild.required(MatSort);
  source = computed(() => {
    const source = new MatTableDataSource(this.projects());
    source.sort = this.sort();
    return source;
  });

  trackBy(idx: number, row: ExtendedProject) {
    return row.Id;
  }

  expandedRows = signal({});
  expandRow(id: string) {
    this.expandedRows.update((expanded) => ({ ...expanded, [id]: !expanded[id] }));
  }

  reload() {
    this.projectService.reload();
    this.agreementService.reload();
    this.productService.reload();
    this.projectsResource.reload();
  }

  async attachInvoices(items: Project[], projectIds: Set<string>): Promise<ExtendedProject[]> {
    const result: ExtendedProject[] = [];

    for (const p of items) {
      const project: ExtendedProject = {
        _Owner: { type: '', id: '' },
        _Invoices: [],
        _ToBeInvoiced: 0,
        _OverdueInvoices: 0,
        _ProductOwner: '',

        ...p,
      };

      const [type, id] = project.Owner.split(':');
      project.Milestones = [];
      project.PurchasedHours = [];
      project._Owner = { type, id }; // used by router in template

      const item =
        type === 'opportunity'
          ? this.productService.products().find((it) => it.JobNumber === id)
          : this.agreementService.agreements().find((it) => it.AgreementNumber === id);
      if (!item) continue;

      const byProject = ({ ProjectId }) =>
        project.Id === project.Owner ? !projectIds.has(ProjectId) : ProjectId === project.Id;

      project.Milestones = ((item as Product).Milestones || []).filter(byProject);
      project.PurchasedHours = (item.PurchasedHours || []).filter(byProject);
      const invoices = [...project.Milestones, ...project.PurchasedHours];

      project._Invoices = invoices;

      // Calculate total to-be-invoiced amount (excluding already invoiced items)
      project._ToBeInvoiced = invoices.reduce((total, { Value, Status, Currency, Rate }) => {
        if (+Status === 20) {
          return total;
        }
        Value = Value || 0;
        Rate = Rate || 1;
        Currency = Currency || 'AUD';
        return total + (Currency === 'AUD' ? Value : Value / Rate);
      }, 0);

      // Calculate total overdue amount (not invoiced and past due date)
      project._OverdueInvoices = invoices.reduce(
        (total, { Value = 0, Status, Currency, Rate, DueDate }) =>
          +Status === 20 || moment(DueDate).isAfter() ? total : total + (Currency === 'AUD' ? Value : Value / Rate),
        0,
      );
      project._ProductOwner = item.ProductOwner;
      // Only include projects that have uninvoiced items
      if (invoices.some((i) => Number(i.Status) !== 20)) {
        result.push(project);
      }
    }
    return result;
  }

  _formatDate(date?: string): string | undefined {
    return date && moment(date).format('MMM DD, YYYY');
  }

  exportProjects() {
    const data = {
      headers: ['Title', 'Description', 'Start Date', 'End Date', 'To Be Invoiced', 'Product Owner'],
      rows: this.projects()
        .filter((item) => item['_notInvoiced'])
        .map((item) => [
          item.Title,
          item.Description,
          this._formatDate(item.StartDate),
          this._formatDate(item.EndDate),
          (item['_ToBeInvoiced'] || 0).toLocaleString(),
          item['_ProductOwner'],
        ]),
    };
    this.exporter.asCsv(data, 'projects');
  }
}
